import type { DuoLanguageAnswer } from '@/components/v2/steps/LanguageGridStep'
import type {
  DuoAnswer,
  DuoBenefit,
  DuoMotif,
} from '@/components/v2/steps/types'

/**
 * li-funnel-redesign v2 — Duolingo's register flow, adapted to LingoChat.
 *
 * The flow is a declarative table of steps rendered by the step-type
 * components, so adding or reordering a screen is a one-line change here.
 * `routes.tsx` turns this table into the route children (in this order), and
 * the onboarding action derives the next step from that route order.
 */

/** Writes the picked value to `localStorage` under this key on submit. */
type StepStorageKey = 'comfortLevel' | 'dailyGoal' | 'selectedLanguage'

interface BaseStep {
  /** Route path, also the step's slug in the A/B route config. */
  path: string
}

/** Every screen that carries a tracked question or statement line. */
interface TitledStep extends BaseStep {
  titleKey: string
}

export type V2Step =
  | (TitledStep & {
      type: 'language'
      answers: DuoLanguageAnswer[]
      storageKey: StepStorageKey
      withLogin?: boolean
    })
  | (TitledStep & {
      type: 'single-select'
      answers: DuoAnswer[]
      motif?: DuoMotif
      storageKey?: StepStorageKey
      subtitleKey?: string
    })
  | (TitledStep & {
      type: 'multi-select'
      answers: DuoAnswer[]
      subtitleKey?: string
    })
  | (TitledStep & { type: 'achievements'; benefits: DuoBenefit[] })
  | (TitledStep & { type: 'mascot'; bodyKey?: string })
  | (TitledStep & {
      type: 'loader'
      bodyKey?: string
      durationMs?: number
      progressLabelKey?: string
    })
  | (BaseStep & { type: 'lesson' })

// The 15 languages of the baseline funnel, with their existing keys and codes
// kept verbatim so `selectedLanguage` stays comparable across variants.
const languageAnswers: DuoLanguageAnswer[] = [
  { emoji: '🇬🇧', title: 'pages.onboarding.languages.en', value: 'en' },
  { emoji: '🇪🇸', title: 'pages.onboarding.languages.es', value: 'es' },
  { emoji: '🇩🇪', title: 'pages.onboarding.languages.de', value: 'de' },
  { emoji: '🇫🇷', title: 'pages.onboarding.languages.fr', value: 'fr' },
  { emoji: '🇮🇹', title: 'pages.onboarding.languages.it', value: 'it' },
  { emoji: '🇵🇹', title: 'pages.onboarding.languages.pt', value: 'pt' },
  { emoji: '🇳🇱', title: 'pages.onboarding.languages.nl', value: 'nl' },
  { emoji: '🇯🇵', title: 'pages.onboarding.languages.ja', value: 'ja' },
  { emoji: '🇨🇳', title: 'pages.onboarding.languages.ch', value: 'zh' },
  { emoji: '🇰🇷', title: 'pages.onboarding.languages.ko', value: 'ko' },
  { emoji: '🇷🇺', title: 'pages.onboarding.languages.ru', value: 'ru' },
  { emoji: '🇮🇩', title: 'pages.onboarding.languages.in', value: 'id' },
  { emoji: '🇵🇱', title: 'pages.onboarding.languages.pl', value: 'pl' },
  { emoji: '🇸🇪', title: 'pages.onboarding.languages.sv', value: 'sv' },
  { emoji: '🇹🇷', title: 'pages.onboarding.languages.tr', value: 'tr' },
]

// Reused verbatim from the baseline WhyQuestionPage.
const whyAnswers: DuoAnswer[] = [
  { emoji: '✈️', title: 'pages.onboarding.whyQuestion.answer1' },
  { emoji: '💼', title: 'pages.onboarding.whyQuestion.answer2' },
  { emoji: '🏠', title: 'pages.onboarding.whyQuestion.answer3' },
  { emoji: '📚', title: 'pages.onboarding.whyQuestion.answer4' },
  { emoji: '🎭', title: 'pages.onboarding.whyQuestion.answer5' },
  { emoji: '🤔', title: 'pages.onboarding.whyQuestion.answer6' },
]

// Reused verbatim from the baseline LevelQuestionPage; the emoji is unused here
// because the rows carry Duolingo's ascending signal bars instead.
const levelAnswers: DuoAnswer[] = [
  {
    emoji: '🐣',
    subtitle: 'pages.onboarding.levelQuestion.answer1Subtitle',
    title: 'pages.onboarding.levelQuestion.answer1',
    value: 'beginner',
  },
  {
    emoji: '🌱',
    subtitle: 'pages.onboarding.levelQuestion.answer2Subtitle',
    title: 'pages.onboarding.levelQuestion.answer2',
    value: 'elementary',
  },
  {
    emoji: '💬',
    subtitle: 'pages.onboarding.levelQuestion.answer3Subtitle',
    title: 'pages.onboarding.levelQuestion.answer3',
    value: 'intermediate',
  },
  {
    emoji: '🚀',
    subtitle: 'pages.onboarding.levelQuestion.answer4Subtitle',
    title: 'pages.onboarding.levelQuestion.answer4',
    value: 'advanced',
  },
]

// Reused verbatim from the baseline TopicsQuestionPage.
const topicsAnswers: DuoAnswer[] = [
  { emoji: '🍳', title: 'pages.onboarding.topicsQuestion.answer1' },
  { emoji: '✈️', title: 'pages.onboarding.topicsQuestion.answer2' },
  { emoji: '💼', title: 'pages.onboarding.topicsQuestion.answer3' },
  { emoji: '💻', title: 'pages.onboarding.topicsQuestion.answer4' },
  { emoji: '⚽', title: 'pages.onboarding.topicsQuestion.answer5' },
  { emoji: '🎨', title: 'pages.onboarding.topicsQuestion.answer6' },
  { emoji: '🔬', title: 'pages.onboarding.topicsQuestion.answer7' },
  { emoji: '👗', title: 'pages.onboarding.topicsQuestion.answer8' },
  { emoji: '🧘', title: 'pages.onboarding.topicsQuestion.answer9' },
  { emoji: '🤔', title: 'pages.onboarding.topicsQuestion.answer10' },
]

const achievementBenefits: DuoBenefit[] = [
  {
    bodyKey: 'pages.onboarding.v2.achievements.converseBody',
    emoji: '💬',
    titleKey: 'pages.onboarding.v2.achievements.converse',
  },
  {
    bodyKey: 'pages.onboarding.v2.achievements.vocabularyBody',
    emoji: '🗂️',
    titleKey: 'pages.onboarding.v2.achievements.vocabulary',
  },
  {
    bodyKey: 'pages.onboarding.v2.achievements.habitBody',
    emoji: '⏰',
    titleKey: 'pages.onboarding.v2.achievements.habit',
  },
]

// Duolingo puts the minutes on the left of the row and the intensity label on
// the right, so the time is the title and the label is the trailing subtitle.
const dailyGoalAnswers: DuoAnswer[] = [
  {
    emoji: '🌤️',
    subtitle: 'pages.onboarding.v2.dailyGoal.casual',
    title: 'pages.onboarding.v2.dailyGoal.casualTime',
    value: 'casual',
  },
  {
    emoji: '⭐',
    subtitle: 'pages.onboarding.v2.dailyGoal.regular',
    title: 'pages.onboarding.v2.dailyGoal.regularTime',
    value: 'regular',
  },
  {
    emoji: '🔥',
    subtitle: 'pages.onboarding.v2.dailyGoal.serious',
    title: 'pages.onboarding.v2.dailyGoal.seriousTime',
    value: 'serious',
  },
  {
    emoji: '🚀',
    subtitle: 'pages.onboarding.v2.dailyGoal.intense',
    title: 'pages.onboarding.v2.dailyGoal.intenseTime',
    value: 'intense',
  },
]

const pathAnswers: DuoAnswer[] = [
  {
    emoji: '📒',
    subtitle: 'pages.onboarding.v2.choosePath.scratchBody',
    title: 'pages.onboarding.v2.choosePath.scratch',
    value: 'scratch',
  },
  {
    emoji: '🧭',
    subtitle: 'pages.onboarding.v2.choosePath.findLevelBody',
    title: 'pages.onboarding.v2.choosePath.findLevel',
    value: 'find-level',
  },
]

/** Minutes behind each daily-goal answer, for the `{{minutes}}` interpolation. */
export const DAILY_GOAL_MINUTES: Record<string, string> = {
  casual: '5',
  regular: '10',
  serious: '15',
  intense: '20',
}

export const DEFAULT_DAILY_GOAL_MINUTES = DAILY_GOAL_MINUTES.regular

// The order Duolingo's own register flow runs in: pick a course, meet the
// mascot, answer the questions, see what you can achieve, set a goal, choose a
// starting point, wait out the path build, get a pep talk, then play the first
// lesson. The create-profile wall that follows is /sign-up/company.
const v2OnboardingSteps: V2Step[] = [
  {
    answers: languageAnswers,
    path: 'question-language',
    storageKey: 'selectedLanguage',
    titleKey: 'pages.onboarding.v2.language.title',
    type: 'language',
    withLogin: true,
  },
  {
    bodyKey: 'pages.onboarding.v2.intro.body',
    path: 'intro',
    titleKey: 'pages.onboarding.v2.intro.title',
    type: 'mascot',
  },
  {
    answers: whyAnswers,
    path: 'why-question',
    titleKey: 'pages.onboarding.whyQuestion.title',
    type: 'single-select',
  },
  {
    answers: levelAnswers,
    motif: 'signal-bars',
    path: 'level-question',
    storageKey: 'comfortLevel',
    titleKey: 'pages.onboarding.v2.proficiency.title',
    type: 'single-select',
  },
  {
    answers: topicsAnswers,
    path: 'topics-question',
    subtitleKey: 'pages.onboarding.topicsQuestion.subtitle',
    titleKey: 'pages.onboarding.topicsQuestion.title',
    type: 'multi-select',
  },
  {
    benefits: achievementBenefits,
    path: 'achievements',
    titleKey: 'pages.onboarding.v2.achievements.title',
    type: 'achievements',
  },
  {
    answers: dailyGoalAnswers,
    motif: 'goal',
    path: 'daily-goal',
    storageKey: 'dailyGoal',
    titleKey: 'pages.onboarding.v2.dailyGoal.title',
    type: 'single-select',
  },
  {
    answers: pathAnswers,
    motif: 'card',
    path: 'choose-path',
    titleKey: 'pages.onboarding.v2.choosePath.title',
    type: 'single-select',
  },
  {
    bodyKey: 'pages.onboarding.v2.pathBuilding.body',
    path: 'path-building',
    progressLabelKey: 'pages.onboarding.v2.pathBuilding.progressLabel',
    titleKey: 'pages.onboarding.v2.pathBuilding.title',
    type: 'loader',
  },
  {
    bodyKey: 'pages.onboarding.v2.commitment.body',
    path: 'commitment',
    titleKey: 'pages.onboarding.v2.commitment.title',
    type: 'mascot',
  },
  // Last step: being last, the action redirects to /sign-up/company — which is
  // Duolingo's "Time to create a profile!" wall.
  { path: 'mini-lesson', type: 'lesson' },
]

export default v2OnboardingSteps
